import React, { useState, useCallback, useMemo, useEffect } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TextField, Checkbox } from '@fluentui/react';
import { HelpButton } from '@bingads-webui-react/help-popup';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SharedLibraryUtils, UETTagScopeEditor } from '@bingads-webui-campaign-react/shared-library-scopes';
import { ClarityGraphInstruction } from '@bingads-webui-campaign-react/clarity-graph-instruction';
import { getStepStyles } from '../../styles/shared-content-style';
import { ClarityUncheckWarningDialog } from '../../shared/clarity-uncheck-warning';

const ClarityTagStates = {
  Enable: 1,
  Disable: 0,
};
const MAX_TAG_NAME_LENGTH = 100;

const CreateUetTagViewUnstyled = (props) => {
  const {
    classes,
    i18n,
    legacyI18n,
    tagEntity,
    tagSharingScopeContext,
    updateTagEntity,
    showError,
    resetShowError,
  } = props;

  const [tagName, setTagName] = useState(tagEntity.Name);
  const [tagDescription, setTagDescription] = useState(tagEntity.Description);
  const { IsClarityTagEnabled } = tagEntity;
  const [isClarityEnabled, setIsClarityEnabled] = useState(_.isNumber(IsClarityTagEnabled)
    ? IsClarityTagEnabled === ClarityTagStates.Enable : false); // render on permissions
  const [selectedSharingOption, setSelectedSharingOption] = useState(tagSharingScopeContext.selectedSharingOption || SharedLibraryUtils.Scopes.CUSTOMER); // render only when creating a tag
  const [tagNameErrorMsg, setTagNameErrorMsg] = useState('');

  const [showClarityWarningDialog, setShowClarityWarningDialog] = useState(false);

  const getTagNameErrorMsg = useCallback((input) => {
    if (!input || input.length === 0) {
      return i18n.getString(_TL_('Enter a name.'));
    }
    if (input.length > MAX_TAG_NAME_LENGTH) {
      return i18n.getString(_TL_('The name is too long. Please shorten it and try again.'));
    }
    return '';
  }, [i18n]);

  useEffect(() => {
    if (showError) {
      setTagNameErrorMsg(getTagNameErrorMsg(tagName));
      resetShowError();
    }
    if (!_.isNumber(IsClarityTagEnabled)) {
      // Update Tag Entity if IsClarityTagEnabled is not set
      // Default is to disable Clarity Tag
      updateTagEntity({ IsClarityTagEnabled: ClarityTagStates.Disable });
    }
  }, [IsClarityTagEnabled, getTagNameErrorMsg, isClarityEnabled, resetShowError, showError, tagName, updateTagEntity]);


  const onChangeTagName = useCallback((event, value) => {
    setTagName(value);
    const errorMsg = getTagNameErrorMsg(value);
    setTagNameErrorMsg(errorMsg);
    updateTagEntity({ Name: _.isEmpty(errorMsg) ? value : null });
  }, [getTagNameErrorMsg, updateTagEntity]);

  const onChangeTagDescription = useCallback((event, value) => {
    setTagDescription(value);
    updateTagEntity({ Description: value });
  }, [updateTagEntity]);

  const onRenderClarityLabel = () => (
    <ClarityGraphInstruction i18n={i18n} />
  );

  const onChangeClarityStatus = useCallback((event, checked) => {
    const isEnabled = !!checked;
    if (!isEnabled) {
      setShowClarityWarningDialog(true);
    } else {
      setIsClarityEnabled(isEnabled);
      updateTagEntity({ IsClarityTagEnabled: isEnabled ? ClarityTagStates.Enable : ClarityTagStates.Disable });
    }
  }, [updateTagEntity]);

  const scopeOptions = useMemo(() => {
    const options = [{
      id: SharedLibraryUtils.Scopes.CUSTOMER,
      text: `${i18n.getString(_TL_('Used only by'))} ${tagSharingScopeContext.tagOwner.Name} (${tagSharingScopeContext.tagOwner.Number})`,
    }, {
      id: SharedLibraryUtils.Scopes.HIERARCHY,
      text: i18n.getString(_TL_('Shared with selected manager accounts')),
    }];
    return options;
  }, [i18n, tagSharingScopeContext.tagOwner.Name, tagSharingScopeContext.tagOwner.Number]);

  const onSelectedOptionChanged = useCallback((option) => {
    setSelectedSharingOption(option);
    updateTagEntity(_.defaults({ selectedSharingOption: option }, option === SharedLibraryUtils.Scopes.CUSTOMER ? { SharingScope: null } : null)); // clean up tree selection if no-sharing selected
  }, [updateTagEntity]);

  const onTreeSelectionUpdated = useCallback((treeNodes) => {
    const hierarchySelection = SharedLibraryUtils.treeToSharingScope(treeNodes);
    updateTagEntity({ SharingScope: hierarchySelection });
  }, [updateTagEntity]);

  const uncheckDialogConfirm = useCallback(() => {
    setIsClarityEnabled(false);
    setShowClarityWarningDialog(false);
    updateTagEntity({ IsClarityTagEnabled: ClarityTagStates.Disable });
  }, [updateTagEntity]);

  const uncheckDialogCancel = useCallback(() => {
    setIsClarityEnabled(true);
    setShowClarityWarningDialog(false);
    updateTagEntity({ IsClarityTagEnabled: ClarityTagStates.Enable });
  }, [updateTagEntity]);

  return (
    <div className={classNames(classes.contentBlock, classes.scrollableContainer)}>
      <div className={classNames(classes.section)}>
        <TextField
          className="tag-name-input"
          label={i18n.getString(_TL_('UET tag name'))}
          ariaLabel={i18n.getString(_TL_('UET tag name'))}
          placeholder={i18n.getString(_TL_('Enter a UET tag name.'))}
          value={tagName}
          onChange={onChangeTagName}
          styles={{ fieldGroup: { width: 600 } }}
        />
        {!_.isEmpty(tagNameErrorMsg) && (
          <div className="row" role="alert">
            <p className={classes.errorText}>{tagNameErrorMsg}</p>
          </div>
        )}
      </div>
      <div className={classes.nonAccordionTitle}>
        <span className="title">{i18n.getString(_TL_('UET tag description'))}</span>
        <HelpButton
          aria-label={i18n.getString(_TL_('UET tag description'))}
          topic="pop_BA_UET_TagDescription"
        />
      </div>
      <TextField
        className={classNames(classes.section, 'tag-description-input')}
        ariaLabel={i18n.getString(_TL_('UET tag description'))}
        placeholder={i18n.getString(_TL_('Enter a UET tag description.'))}
        value={tagDescription}
        onChange={onChangeTagDescription}
        styles={{ fieldGroup: { width: 600 } }}
      />
      <div className="clarity-selection-section">
        <div className={classes.nonAccordionTitle}>
          <span className="title">{i18n.getString(_TL_('Enable Microsoft Clarity'))}</span>
          <HelpButton
            aria-label={i18n.getString(_TL_('Enable Microsoft Clarity'))}
            topic="pop_BA_UET_EnableClarity"
          />
        </div>
        <Checkbox
          className={classNames(classes.section, 'clarity-checkbox')}
          label={i18n.getString(_TL_('Microsoft Clarity brings advertisers actionable user behavioral insights to optimize their ad campaigns. By enabling Clarity tracking, you agree to the Clarity terms of use and privacy policy.'))}
          onRenderLabel={onRenderClarityLabel}
          checked={isClarityEnabled}
          onChange={onChangeClarityStatus}
        />
        <ClarityUncheckWarningDialog
          i18n={i18n}
          checkIncludeCallback={uncheckDialogConfirm}
          uncheckIncludeCallback={uncheckDialogCancel}
          shouldShowDialog={showClarityWarningDialog}
        />
      </div>
      {tagSharingScopeContext.isTagSharingScope &&
        <div>
          <div className={classes.nonAccordionTitle}>
            <span className="title">{i18n.getString(_TL_('Sharing'))}</span>
            <HelpButton
              aria-label={i18n.getString(_TL_('Sharing'))}
              topic="pop_BA_UET_Hierarchies_Sharing"
            />
          </div>
          <div className={classNames(classes.scopeEditor, 'uet-scope-editor')}>
            <UETTagScopeEditor
              customerHierarchy={tagSharingScopeContext.customerHierarchy}
              onSelectedOptionChanged={onSelectedOptionChanged}
              onTreeSelectionUpdated={onTreeSelectionUpdated}
              deps={{ i18n: legacyI18n }}
              tagEntity={{
                Owner: tagSharingScopeContext.tagOwner,
                SharingScope: tagSharingScopeContext.sharingScope,
              }}
              scopeOptions={scopeOptions}
              selectedOption={selectedSharingOption}
            />
          </div>
        </div>
      }
    </div>
  );
};

CreateUetTagViewUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(getStepStyles(), _.constant(PropTypes.string.isRequired)))
    .isRequired,
  i18n: PropTypes.objectOf(PropTypes.any).isRequired,
  legacyI18n: PropTypes.objectOf(PropTypes.any).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  tagEntity: PropTypes.objectOf(PropTypes.any).isRequired,
  tagSharingScopeContext: PropTypes.shape({
    isTagSharingScope: PropTypes.bool.isRequired,
    selectedSharingOption: PropTypes.string,
    customerHierarchy: PropTypes.arrayOf(PropTypes.any),
    tagOwner: PropTypes.objectOf(PropTypes.any),
    sharingScope: PropTypes.objectOf(PropTypes.any),
  }).isRequired,
  updateTagEntity: PropTypes.func.isRequired,
  showError: PropTypes.bool.isRequired,
  resetShowError: PropTypes.func.isRequired,
};

export const CreateUetTagView = withDefaultStyles(CreateUetTagViewUnstyled, getStepStyles);
